import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  LeaveAttachment, LeaveListBody, LeaveRequestItem, LeaveStatusCounts,
  LeaveStatusFilter, LeaveType, ParentLeaveRequestService,
} from './parent-leave-request.service';

const PAGE_LIMIT = 10;
const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;

// Leave Request — list (status filter pills + pagination), Apply Leave
// modal (leave type/date/time/reason/attachments), and a detail view
// (status, approver/canceller info, remarks, attachment preview, cancel
// action for pending requests). Rendered conditionally inside
// ParentLoginComponent's dashboard, same pattern as ParentDiaryComponent/
// ParentAttendanceComponent (no routing).
@Component({
  selector: 'app-parent-leave-request',
  templateUrl: './parent-leave-request.component.html',
  styleUrls: ['./parent-leave-request.component.css'],
})
export class ParentLeaveRequestComponent implements OnInit, OnChanges {

  // The backend resolves student/class from the auth token, not request
  // params, so this component has no real data dependency on studentId —
  // it exists purely so ParentLoginComponent's studentSwitchTick (bumped on
  // every switchStudent()) can trigger ngOnChanges and force a reload, the
  // same way a real @Input() studentId change does for diary/attendance/
  // timetable.
  @Input() studentSwitchTick = 0;

  activeStatus: LeaveStatusFilter = 'ALL';
  items: LeaveRequestItem[] = [];
  statusCounts: LeaveStatusCounts = { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
  totalCount = 0;
  maxLeaveDaysAllowed = 0;

  currentPage = 1;
  maxPageNumber = 1;
  listLoading = false;
  listError = '';
  private listRequestId = 0;

  selectedItem: LeaveRequestItem | null = null;

  // Apply Leave modal
  showApplyModal = false;
  leaveTypes: LeaveType[] = [];
  leaveTypesLoading = false;
  selectedLeaveTypeId: number | null = null;
  startDate = '';
  endDate = '';
  startTime = '';
  endTime = '';
  applyReason = '';
  attachmentFiles: File[] = [];
  submitting = false;
  applyError = '';

  // Cancel confirm
  cancellingItem: LeaveRequestItem | null = null;
  cancelReason = '';
  cancelling = false;
  cancelError = '';

  // Attachment preview (image/PDF), same small-window pattern as the diary
  // component's media viewers.
  imageViewerUrl: string | null = null;
  pdfViewerUrl: SafeResourceUrl | null = null;

  constructor(private leaveService: ParentLeaveRequestService, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentSwitchTick'] && !changes['studentSwitchTick'].firstChange) {
      this.activeStatus = 'ALL';
      this.currentPage = 1;
      this.selectedItem = null;
      this.loadList();
    }
  }

  private get lang(): string {
    // Deliberately always 'en', not the UI's selected display language —
    // the parent mobile app's backend endpoints expect this constant
    // regardless of what language the user has the interface in; switching
    // the UI language must never change this API payload field.
    return 'en';
  }

  get selectedLeaveType(): LeaveType | undefined {
    return this.leaveTypes.find(t => t.leaveTypeId === this.selectedLeaveTypeId);
  }

  setStatus(status: LeaveStatusFilter): void {
    if (this.activeStatus === status) { return; }
    this.activeStatus = status;
    this.currentPage = 1;
    this.loadList();
  }

  loadList(): void {
    this.listLoading = true;
    this.listError = '';
    // Bumped on every call (including the one ngOnChanges fires on a
    // student switch) so a still-in-flight request from the PREVIOUS
    // student can't land after the new student's request and overwrite its
    // fresher data with stale data — without this, switching student while
    // this screen's initial load hadn't finished yet would briefly show
    // the new student's list and then flip back to the old one.
    const requestId = ++this.listRequestId;
    this.leaveService.getLeaveRequestList(this.activeStatus, this.currentPage, PAGE_LIMIT).subscribe({
      next: (res: any) => {
        if (requestId !== this.listRequestId) { return; }
        this.listLoading = false;
        if (res && res.status && res.body) {
          const body: LeaveListBody = res.body;
          this.items = body.leaveRequests || [];
          this.statusCounts = body.statusCounts || { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
          this.totalCount = body.totalCount || 0;
          this.maxPageNumber = body.maxPageNumber || 1;
          this.maxLeaveDaysAllowed = body.maxLeaveDaysAllowed || 0;
        } else {
          this.items = [];
          this.listError = 'Unable to load leave requests.';
        }
      },
      error: () => {
        if (requestId !== this.listRequestId) { return; }
        this.listLoading = false;
        this.listError = 'Unable to load leave requests.';
      },
    });
  }

  goToPrevPage(): void {
    if (this.currentPage <= 1) { return; }
    this.currentPage--;
    this.loadList();
  }

  goToNextPage(): void {
    if (this.currentPage >= this.maxPageNumber) { return; }
    this.currentPage++;
    this.loadList();
  }

  trackByLeaveId(_: number, item: LeaveRequestItem): number {
    return item.leaveRequestId;
  }

  statusChipClass(statusSlug: string): string {
    switch ((statusSlug || '').toLowerCase()) {
      case 'approved': return 'plr-chip-green';
      case 'pending': return 'plr-chip-yellow';
      case 'rejected': return 'plr-chip-red';
      default: return 'plr-chip-grey';
    }
  }

  // startDate/endDate arrive as raw ISO (YYYY-MM-DD) from the API — format
  // them into a readable "24 Sep 2026" form rather than showing the ISO
  // string as-is.
  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) { return ''; }
    const parsed = new Date(`${dateStr}T00:00:00`);
    if (isNaN(parsed.getTime())) { return dateStr; }
    return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  dateRangeLabel(item: LeaveRequestItem): string {
    if (!item.endDate || item.startDate === item.endDate) { return this.formatDate(item.startDate); }
    return `${this.formatDate(item.startDate)} - ${this.formatDate(item.endDate)}`;
  }

  // Day-number/month-abbreviation/weekday breakdown for the date box shown
  // on each row — startDate is ISO (YYYY-MM-DD), parsed natively since no
  // date library is otherwise used in this component.
  dateBox(item: LeaveRequestItem): { day: string; month: string; weekday: string } | null {
    const parsed = new Date(`${item.startDate}T00:00:00`);
    if (isNaN(parsed.getTime())) { return null; }
    return {
      day: String(parsed.getDate()),
      month: parsed.toLocaleDateString('en-US', { month: 'short' }),
      weekday: parsed.toLocaleDateString('en-US', { weekday: 'short' }),
    };
  }

  // "Date passed" warning — only meaningful while the request is still
  // Pending (Approved/Rejected/Cancelled already carry their own outcome).
  isDatePassed(item: LeaveRequestItem): boolean {
    if ((item.statusSlug || '').toLowerCase() !== 'pending') { return false; }
    const parsed = new Date(`${item.startDate}T00:00:00`);
    if (isNaN(parsed.getTime())) { return false; }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parsed.getTime() < today.getTime();
  }

  // Section/group label for an item in the "ALL" tab — pending items are
  // pulled into their own "Waiting for approval" group (handled separately
  // in the template via `pendingItems`/`groupedItems`); everything else is
  // grouped by the month/year of its start date, matching the screenshot's
  // "September 2026" section headers.
  monthGroupLabel(item: LeaveRequestItem): string {
    const parsed = new Date(`${item.startDate}T00:00:00`);
    if (isNaN(parsed.getTime())) { return ''; }
    return parsed.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  get pendingItems(): LeaveRequestItem[] {
    return this.items.filter(i => (i.statusSlug || '').toLowerCase() === 'pending');
  }

  // Non-pending items, grouped by month/year, preserving the order items
  // already arrive in from the API within each group.
  get groupedNonPendingItems(): { label: string; items: LeaveRequestItem[] }[] {
    const nonPending = this.items.filter(i => (i.statusSlug || '').toLowerCase() !== 'pending');
    const groups: { label: string; items: LeaveRequestItem[] }[] = [];
    for (const item of nonPending) {
      const label = this.monthGroupLabel(item);
      let group = groups.find(g => g.label === label);
      if (!group) {
        group = { label, items: [] };
        groups.push(group);
      }
      group.items.push(item);
    }
    return groups;
  }

  openDetail(item: LeaveRequestItem): void {
    this.selectedItem = item;
  }

  closeDetail(): void {
    this.selectedItem = null;
  }

  // ---------- Apply Leave modal ----------

  openApplyModal(): void {
    this.showApplyModal = true;
    this.applyError = '';
    this.selectedLeaveTypeId = null;
    this.startDate = '';
    this.endDate = '';
    this.startTime = '';
    this.endTime = '';
    this.applyReason = '';
    this.attachmentFiles = [];
    this.loadLeaveTypes();
  }

  closeApplyModal(): void {
    if (this.submitting) { return; }
    this.showApplyModal = false;
  }

  private loadLeaveTypes(): void {
    this.leaveTypesLoading = true;
    this.leaveService.getLeaveTypes(this.lang).subscribe({
      next: (res: any) => {
        this.leaveTypesLoading = false;
        this.leaveTypes = (res && res.status && res.body && res.body.leaveTypes) || [];
      },
      error: () => {
        this.leaveTypesLoading = false;
        this.leaveTypes = [];
      },
    });
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    for (const file of files) {
      if (this.attachmentFiles.length >= MAX_ATTACHMENTS) {
        this.applyError = `You can attach up to ${MAX_ATTACHMENTS} files.`;
        break;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        this.applyError = `${file.name} exceeds the 50MB size limit.`;
        continue;
      }
      this.attachmentFiles.push(file);
    }
  }

  removeAttachment(index: number): void {
    this.attachmentFiles.splice(index, 1);
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  submitLeaveRequest(): void {
    this.applyError = '';
    if (!this.selectedLeaveTypeId) {
      this.applyError = 'Please select a leave type.';
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.applyError = 'Please select start and end dates.';
      return;
    }
    if (this.endDate < this.startDate) {
      this.applyError = 'End date must be on or after the start date.';
      return;
    }
    const type = this.selectedLeaveType;
    if (type?.isStartTimeRequired && !this.startTime) {
      this.applyError = 'Please select a start time.';
      return;
    }
    if (type?.isEndTimeRequired && !this.endTime) {
      this.applyError = 'Please select an end time.';
      return;
    }
    if (type?.isStartTimeRequired && type?.isEndTimeRequired && this.startTime && this.endTime && this.endTime <= this.startTime) {
      this.applyError = 'End time must be after the start time.';
      return;
    }
    if (!this.applyReason.trim()) {
      this.applyError = 'Please enter a reason.';
      return;
    }

    const formData = new FormData();
    formData.append('leaveTypeId', String(this.selectedLeaveTypeId));
    formData.append('startDate', this.startDate);
    formData.append('endDate', this.endDate);
    formData.append('startTime', type?.isStartTimeRequired ? this.startTime : '');
    formData.append('endTime', type?.isEndTimeRequired ? this.endTime : '');
    formData.append('reason', this.applyReason.trim());
    this.attachmentFiles.forEach(file => formData.append('attachments', file));

    this.submitting = true;
    this.leaveService.submitLeaveRequest(formData).subscribe({
      next: (res: any) => {
        this.submitting = false;
        if (res && res.status) {
          this.showApplyModal = false;
          this.currentPage = 1;
          this.loadList();
        } else {
          this.applyError = res?.message || 'Unable to submit leave request.';
        }
      },
      error: (err) => {
        this.submitting = false;
        this.applyError = err?.error?.message || 'Unable to submit leave request.';
      },
    });
  }

  // ---------- Cancel ----------

  requestCancel(item: LeaveRequestItem, event?: Event): void {
    event?.stopPropagation();
    this.cancellingItem = item;
    this.cancelReason = '';
    this.cancelError = '';
  }

  closeCancelModal(): void {
    if (this.cancelling) { return; }
    this.cancellingItem = null;
  }

  confirmCancel(): void {
    if (!this.cancellingItem) { return; }
    if (!this.cancelReason.trim()) {
      this.cancelError = 'Please enter a reason.';
      return;
    }
    this.cancelError = '';
    this.cancelling = true;
    const leaveRequestId = this.cancellingItem.leaveRequestId;
    this.leaveService.cancelLeaveRequest(leaveRequestId, this.cancelReason.trim()).subscribe({
      next: (res: any) => {
        this.cancelling = false;
        const body = res?.body || {};
        const idx = this.items.findIndex(i => i.leaveRequestId === leaveRequestId);
        if (idx !== -1) {
          const updated: LeaveRequestItem = {
            ...this.items[idx],
            displayStatus: body.displayStatus || 'Cancelled',
            statusSlug: (body.displayStatus || 'cancelled').toLowerCase(),
            cancelledDate: body.cancelledDate || this.items[idx].cancelledDate,
            cancelledTime: body.cancelledTime || this.items[idx].cancelledTime,
            remarks: this.cancelReason.trim() || this.items[idx].remarks,
          };
          this.items = [...this.items.slice(0, idx), updated, ...this.items.slice(idx + 1)];
          if (this.selectedItem?.leaveRequestId === leaveRequestId) {
            this.selectedItem = updated;
          }
          this.statusCounts = {
            ...this.statusCounts,
            pending: Math.max(0, this.statusCounts.pending - 1),
            cancelled: this.statusCounts.cancelled + 1,
          };
        }
        this.cancellingItem = null;
      },
      error: () => {
        this.cancelling = false;
      },
    });
  }

  // ---------- Attachment preview ----------

  isImageAttachment(att: LeaveAttachment): boolean {
    return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(att?.attachmentUrl || '');
  }

  openAttachment(att: LeaveAttachment): void {
    if (this.isImageAttachment(att)) {
      this.imageViewerUrl = att.attachmentUrl;
    } else {
      this.pdfViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(att.attachmentUrl);
    }
  }

  closeImageViewer(): void {
    this.imageViewerUrl = null;
  }

  closePdfViewer(): void {
    this.pdfViewerUrl = null;
  }
}
